import React, { useState } from "react";
import {
  Box,
  Typography,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  Card,
  CardContent,
  LinearProgress,
  Stack,
} from "@mui/material";
import type { EmployeeAgreementFileResult } from "@services/riskAnalysis";

const EA_SLATE = "#2c4e60";
const EA_TEAL_HEAD = "#3e829a";
const EA_BORDER = "#d1d5db";

const tableHeadSx = {
  fontWeight: 700,
  fontFamily: "Rethink Sans, sans-serif",
  color: "#fff",
  bgcolor: EA_TEAL_HEAD,
  textTransform: "uppercase" as const,
  letterSpacing: "0.04em",
  fontSize: "12px",
  borderColor: EA_BORDER,
};

function payorExtractionStatusLabel(
  row: EmployeeAgreementFileResult | undefined,
): string {
  if (!row) return "—";
  switch (row.status) {
    case "processed":
      return "Processed";
    case "empty":
      return "No usable text";
    case "failed":
      return "Failed";
    case "extracting":
      return "Extracting…";
    case "queued":
      return "Queued";
    default:
      return String(row.status);
  }
}

/**
 * Renders `phase_narrative_bundle` from the API: markdown-style `###` line headers
 * become styled section titles instead of showing raw hashes.
 */
function PhaseNarrativeContent({ text }: { text: string }): React.ReactElement {
  const trimmed = text.trim();
  if (!trimmed) {
    return <Typography sx={{ fontFamily: "Rethink Sans, sans-serif" }}>—</Typography>;
  }

  const parts = trimmed.split(/^#{1,6}\s*/gm);
  const hasHeadingBreaks = parts.length > 1;

  if (!hasHeadingBreaks) {
    return (
      <Typography sx={{ whiteSpace: "pre-wrap", fontFamily: "Rethink Sans, sans-serif" }}>
        {trimmed}
      </Typography>
    );
  }

  const blocks: React.ReactNode[] = [];
  let k = 0;

  const preamble = parts[0]?.trim();
  if (preamble) {
    blocks.push(
      <Typography
        key={k}
        sx={{ whiteSpace: "pre-wrap", fontFamily: "Rethink Sans, sans-serif", mb: 2 }}
      >
        {preamble}
      </Typography>,
    );
    k += 1;
  }

  for (let i = 1; i < parts.length; i += 1) {
    const segment = parts[i].trim();
    if (!segment) continue;
    const nl = segment.indexOf("\n");
    const titleLine = nl === -1 ? segment : segment.slice(0, nl).trim();
    const body = nl === -1 ? "" : segment.slice(nl + 1).trim();
    blocks.push(
      <Box
        key={k}
        sx={{
          mb: 2.5,
          pl: 2,
          borderLeft: `3px solid ${EA_TEAL_HEAD}`,
        }}
      >
        <Typography
          component="h3"
          sx={{
            fontFamily: "Rethink Sans, sans-serif",
            fontWeight: 700,
            color: EA_TEAL_HEAD,
            fontSize: "1rem",
            mb: body ? 1 : 0,
            lineHeight: 1.35,
          }}
        >
          {titleLine.replace(/:\s*$/, "")}
        </Typography>
        {body ? (
          <Typography
            sx={{
              whiteSpace: "pre-wrap",
              fontFamily: "Rethink Sans, sans-serif",
              color: EA_SLATE,
              lineHeight: 1.65,
            }}
          >
            {body}
          </Typography>
        ) : null}
      </Box>,
    );
    k += 1;
  }

  return <Box>{blocks}</Box>;
}

function TabPanel(props: {
  children?: React.ReactNode;
  index: number;
  value: number;
}): React.ReactElement {
  const { children, value, index } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`payor-contract-tabpanel-${index}`}
      aria-labelledby={`payor-contract-tab-${index}`}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

/** Stable display order for payer contract score dimensions. */
const PAYOR_SCORE_KEYS: readonly string[] = [
  "revenue_risk",
  "compliance_risk",
  "operational_burden",
  "interoperability_gap",
  "negotiation_priority",
  "audit_recoupment_risk",
  "prior_authorization_risk",
  "documentation_coding_risk",
  "overall_provider_advantage",
  "data_completeness_confidence",
];

const PAYOR_SCORE_LABELS: Record<string, string> = {
  revenue_risk: "Revenue risk",
  compliance_risk: "Compliance risk",
  operational_burden: "Operational burden",
  interoperability_gap: "Interoperability gap",
  negotiation_priority: "Negotiation priority",
  audit_recoupment_risk: "Audit / recoupment risk",
  prior_authorization_risk: "Prior authorization risk",
  documentation_coding_risk: "Documentation / coding risk",
  overall_provider_advantage: "Overall provider advantage",
  data_completeness_confidence: "Data completeness confidence",
};

function payorScoreDimensionLabel(key: string): string {
  if (PAYOR_SCORE_LABELS[key]) return PAYOR_SCORE_LABELS[key];
  return key
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === "object" && !Array.isArray(v);
}

function PayorContractScoresDisplay(props: {
  scores: Record<string, unknown> | null;
}): React.ReactElement {
  const { scores } = props;

  if (!scores) {
    return (
      <Typography sx={{ color: "text.secondary", fontFamily: "Rethink Sans, sans-serif" }}>
        No scores returned for this assessment.
      </Typography>
    );
  }

  const keysToShow = [
    ...PAYOR_SCORE_KEYS.filter((k) => Object.prototype.hasOwnProperty.call(scores, k)),
    ...Object.keys(scores).filter((k) => !PAYOR_SCORE_KEYS.includes(k)),
  ];

  if (keysToShow.length === 0) {
    return (
      <Typography sx={{ color: "text.secondary", fontFamily: "Rethink Sans, sans-serif" }}>
        No score dimensions in this response.
      </Typography>
    );
  }

  const fieldSx = {
    fontFamily: "Rethink Sans, sans-serif" as const,
    fontSize: "13px",
  };

  return (
    <Stack spacing={2}>
      {keysToShow.map((key) => {
        const label = payorScoreDimensionLabel(key);
        const raw = scores[key];

        if (raw === null || raw === undefined) {
          return (
            <Paper
              key={key}
              variant="outlined"
              sx={{
                p: 1.5,
                borderColor: EA_BORDER,
                bgcolor: "action.hover",
              }}
            >
              <Typography sx={{ ...fieldSx, fontWeight: 700, color: EA_SLATE }}>{label}</Typography>
              <Typography sx={{ ...fieldSx, color: "text.secondary", mt: 0.5 }}>
                Not assessed
              </Typography>
            </Paper>
          );
        }

        if (!isPlainObject(raw)) {
          return null;
        }

        const numScore =
          typeof raw.score === "number" && Number.isFinite(raw.score) ? raw.score : null;
        const evidence = raw.evidence != null ? String(raw.evidence) : "";
        const confidence = raw.confidence != null ? String(raw.confidence) : "";
        const explanation = raw.explanation != null ? String(raw.explanation) : "";
        const improvement =
          raw.improvement_actions != null ? String(raw.improvement_actions) : "";

        return (
          <Card
            key={key}
            variant="outlined"
            sx={{ borderColor: EA_BORDER, overflow: "visible" }}
          >
            <CardContent sx={{ "&:last-child": { pb: 2 } }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  gap: 1,
                  mb: numScore !== null ? 1 : 0,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "Rethink Sans, sans-serif",
                    fontWeight: 700,
                    color: EA_SLATE,
                    fontSize: "15px",
                  }}
                >
                  {label}
                </Typography>
                {confidence ? (
                  <Chip label={confidence} size="small" variant="outlined" sx={fieldSx} />
                ) : null}
              </Box>
              {numScore !== null ? (
                <Box sx={{ mb: 1.5 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                    <Typography sx={{ ...fieldSx, color: "text.secondary" }}>Score</Typography>
                    <Typography sx={{ ...fieldSx, fontWeight: 700 }}>{numScore}</Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(100, Math.max(0, numScore))}
                    sx={{
                      height: 10,
                      borderRadius: 1,
                      bgcolor: `${EA_BORDER}`,
                      "& .MuiLinearProgress-bar": {
                        borderRadius: 1,
                        bgcolor: EA_TEAL_HEAD,
                      },
                    }}
                  />
                </Box>
              ) : null}
              {evidence ? (
                <Typography sx={{ ...fieldSx, mb: 1 }}>
                  <Box component="span" sx={{ fontWeight: 600, color: EA_SLATE }}>
                    Evidence:{" "}
                  </Box>
                  {evidence}
                </Typography>
              ) : null}
              {explanation ? (
                <Typography sx={{ ...fieldSx, mb: 1, whiteSpace: "pre-wrap" }}>
                  <Box component="span" sx={{ fontWeight: 600, color: EA_SLATE }}>
                    Explanation:{" "}
                  </Box>
                  {explanation}
                </Typography>
              ) : null}
              {improvement ? (
                <Typography sx={{ ...fieldSx, whiteSpace: "pre-wrap" }}>
                  <Box component="span" sx={{ fontWeight: 600, color: EA_SLATE }}>
                    Improvement actions:{" "}
                  </Box>
                  {improvement}
                </Typography>
              ) : null}
            </CardContent>
          </Card>
        );
      })}
    </Stack>
  );
}

export function PayorContractResults(props: {
  analysis: Record<string, unknown> | null;
  loading: boolean;
  statusFiles: File[];
  statusFileResults?: EmployeeAgreementFileResult[] | null;
  /** When set, shown at the top of the results panel (e.g. history upload time or run completion time). */
  analysedAtDisplay?: string | null;
}): React.ReactElement {
  const { analysis, loading, statusFiles, statusFileResults, analysedAtDisplay } = props;
  const [tab, setTab] = useState(0);

  const assessment =
    analysis?.assessment && typeof analysis.assessment === "object"
      ? (analysis.assessment as Record<string, unknown>)
      : null;
  const completeness =
    analysis?.document_completeness_response &&
    typeof analysis.document_completeness_response === "object"
      ? (analysis.document_completeness_response as Record<string, unknown>)
      : null;

  const inventory = (
    Array.isArray(assessment?.document_inventory_rows)
      ? assessment?.document_inventory_rows
      : []
  ) as Record<string, unknown>[];
  const gaps = (
    Array.isArray(assessment?.critical_gaps) ? assessment?.critical_gaps : []
  ) as Record<string, unknown>[];
  const alerts = (
    Array.isArray(assessment?.unicomply_alert_rules)
      ? assessment?.unicomply_alert_rules
      : []
  ) as Record<string, unknown>[];
  const scores = assessment?.scores && typeof assessment.scores === "object"
    ? (assessment.scores as Record<string, unknown>)
    : null;
  const narrative =
    typeof assessment?.phase_narrative_bundle === "string"
      ? assessment.phase_narrative_bundle
      : "";

  if (loading && !analysis) {
    // Per-file status lives in PayorContractAnalysis (ExtractionProgressTable in the upload zone).
    return (
      <Typography sx={{ fontFamily: "Rethink Sans, sans-serif", py: 2 }}>
        Analyzing contract language…
      </Typography>
    );
  }

  if (!analysis) {
    return (
      <Typography sx={{ fontFamily: "Rethink Sans, sans-serif", py: 2 }}>
        No analysis to display.
      </Typography>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      {analysedAtDisplay ? (
        <Typography
          sx={{
            mb: 1.5,
            fontFamily: "Rethink Sans, sans-serif",
            fontSize: "13px",
            color: EA_SLATE,
            fontWeight: 600,
          }}
        >
          Analysed: {analysedAtDisplay}
        </Typography>
      ) : null}
      {statusFileResults && statusFileResults.length > 0 ? (
        <TableContainer component={Paper} sx={{ mb: 2, border: `1px solid ${EA_BORDER}` }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={tableHeadSx}>File</TableCell>
                <TableCell sx={tableHeadSx}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(statusFiles.length
                ? statusFiles.map((f) => f.name)
                : statusFileResults.map((r) => r.filename)
              ).map((name, i) => {
                const row = statusFileResults.find((r) => r.filename === name);
                return (
                  <TableRow key={`${name}-${i}`}>
                    <TableCell
                      sx={{
                        fontFamily: "Rethink Sans, sans-serif",
                        verticalAlign: "top",
                        wordBreak: "break-word",
                      }}
                    >
                      {name}
                    </TableCell>
                    <TableCell sx={{ fontFamily: "Rethink Sans, sans-serif" }}>
                      {payorExtractionStatusLabel(row)}
                      {row?.error ? (
                        <Typography variant="caption" sx={{ color: "#c62828", display: "block", mt: 0.5 }}>
                          {row.error}
                        </Typography>
                      ) : null}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      ) : null}

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          minHeight: 40,
          "& .MuiTab-root": {
            fontFamily: "Rethink Sans, sans-serif",
            textTransform: "none",
            fontWeight: 600,
          },
        }}
      >
        <Tab label="Summary" />
        <Tab label="Tables" />
        <Tab label="Alerts & scores" />
        <Tab label="Detail narrative" />
      </Tabs>

      <TabPanel value={tab} index={0}>
        {completeness ? (
          <Box sx={{ fontFamily: "Rethink Sans, sans-serif" }}>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Document package incomplete — full assessment not available.
            </Alert>
            <Typography sx={{ fontWeight: 700, color: EA_SLATE, mb: 1 }}>
              What was found
            </Typography>
            <Typography sx={{ mb: 2, whiteSpace: "pre-wrap" }}>
              {String(completeness.what_was_found ?? "")}
            </Typography>
            <Typography sx={{ fontWeight: 700, color: EA_SLATE, mb: 1 }}>
              User message
            </Typography>
            <Typography sx={{ whiteSpace: "pre-wrap" }}>
              {String(completeness.user_facing_request_message ?? "")}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ fontFamily: "Rethink Sans, sans-serif" }}>
            <Typography sx={{ fontWeight: 700, color: EA_SLATE, mb: 1 }}>
              Executive summary
            </Typography>
            <Typography sx={{ mb: 2, whiteSpace: "pre-wrap" }}>
              {String(assessment?.executive_summary ?? "")}
            </Typography>
            <Typography sx={{ fontWeight: 700, color: EA_SLATE, mb: 0.5 }}>
              Final recommendation
            </Typography>
            <Typography sx={{ mb: 1 }}>
              {String(assessment?.final_recommendation ?? "")}
            </Typography>
            <Typography sx={{ fontWeight: 700, color: EA_SLATE, mb: 0.5 }}>
              Overall risk
            </Typography>
            <Typography>{String(assessment?.overall_risk_rating ?? "")}</Typography>
          </Box>
        )}
      </TabPanel>

      <TabPanel value={tab} index={1}>
        <Typography
          sx={{ fontWeight: 700, color: EA_SLATE, mb: 1, fontFamily: "Rethink Sans, sans-serif" }}
        >
          Document inventory
        </Typography>
        <TableContainer component={Paper} sx={{ mb: 3, border: `1px solid ${EA_BORDER}` }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={tableHeadSx}>Document</TableCell>
                <TableCell sx={tableHeadSx}>Type</TableCell>
                <TableCell sx={tableHeadSx}>Notes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {inventory.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3}>No rows</TableCell>
                </TableRow>
              ) : (
                inventory.map((row, idx) => {
                  const isChild =
                    String(row.inventory_row_kind ?? "").toLowerCase() ===
                    "logical_child";
                  const cellPad = isChild ? { pl: 3 } : {};
                  return (
                    <TableRow key={idx}>
                      <TableCell sx={cellPad}>
                        {String(row.document_name ?? "")}
                      </TableCell>
                      <TableCell sx={cellPad}>
                        {String(row.document_type ?? "")}
                      </TableCell>
                      <TableCell sx={cellPad}>
                        {String(row.notes ?? "")}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Typography
          sx={{ fontWeight: 700, color: EA_SLATE, mb: 1, fontFamily: "Rethink Sans, sans-serif" }}
        >
          Critical gaps
        </Typography>
        <TableContainer component={Paper} sx={{ border: `1px solid ${EA_BORDER}` }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={tableHeadSx}>Gap</TableCell>
                <TableCell sx={tableHeadSx}>Risk</TableCell>
                <TableCell sx={tableHeadSx}>Owner</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {gaps.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3}>No rows</TableCell>
                </TableRow>
              ) : (
                gaps.map((row, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{String(row.gap ?? "")}</TableCell>
                    <TableCell>{String(row.risk_level ?? "")}</TableCell>
                    <TableCell>{String(row.workflow_owner ?? "")}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      <TabPanel value={tab} index={2}>
        <Typography
          sx={{ fontWeight: 700, color: EA_SLATE, mb: 1, fontFamily: "Rethink Sans, sans-serif" }}
        >
          UniComply alert rules
        </Typography>
        <TableContainer component={Paper} sx={{ mb: 2, border: `1px solid ${EA_BORDER}` }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={tableHeadSx}>Title</TableCell>
                <TableCell sx={tableHeadSx}>Type</TableCell>
                <TableCell sx={tableHeadSx}>Severity</TableCell>
                <TableCell sx={tableHeadSx}>Owner</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {alerts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4}>No rows</TableCell>
                </TableRow>
              ) : (
                alerts.map((row, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{String(row.alert_title ?? "")}</TableCell>
                    <TableCell>{String(row.alert_type ?? "")}</TableCell>
                    <TableCell>{String(row.severity ?? "")}</TableCell>
                    <TableCell>{String(row.owner ?? "")}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Typography
          sx={{ fontWeight: 700, color: EA_SLATE, mb: 1, fontFamily: "Rethink Sans, sans-serif" }}
        >
          Assessment scores
        </Typography>
        <PayorContractScoresDisplay scores={scores} />
      </TabPanel>

      <TabPanel value={tab} index={3}>
        <PhaseNarrativeContent text={narrative} />
      </TabPanel>
    </Box>
  );
}
